import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Star, Award, Globe, Users, MessageCircle, ArrowRight, Sparkles, Quote, ShieldCheck, Phone, X } from "lucide-react";
import { api, inr } from "../lib/api";
import { useSettings } from "../lib/settings";
import Marquee from "../components/Marquee";

const PACKAGE_ICON = {
  basic: "🏗️", premium: "⭐", luxury: "💎",
};

const HOME_BADGE_LABEL = {
  "popular": "Most Popular",
  "recommended": "Recommended",
  "best-value": "Best Value",
  "new-launch": "New Launch",
  "limited-offer": "Limited Offer",
  "premium-choice": "Premium Choice",
};

function PackageCard({ s, idx }) {
  // Resolve badge: prefer admin's `badge` field, fallback to legacy is_popular for backward compat.
  const badgeKey = s.badge || (s.is_popular ? "popular" : "");
  const badgeLabel = HOME_BADGE_LABEL[badgeKey] || "";
  const badgeColor = s.badge_color || "#F7941D";
  const showBadge = !!badgeLabel;
  // Highlight ring only for "popular" badge (preserves existing premium-card look).
  const highlight = badgeKey === "popular";
  const discount = s.original_price > s.price ? Math.round((1 - s.price / s.original_price) * 100) : 0;
  const tierThemes = {
    basic:   { btn: "bg-[#1a3c5e] hover:bg-[#122a42] text-white", accent: "text-[#1a3c5e]" },
    premium: { btn: "bg-[#F7941D] hover:bg-[#e0861a] text-white", accent: "text-[#F7941D]" },
    luxury:  { btn: "bg-[#D32F2F] hover:bg-[#b71c1c] text-white", accent: "text-[#D32F2F]" },
  };
  const theme = tierThemes[s.slug] || tierThemes.basic;

  return (
    <Link
      to={`/services/${s.slug}`}
      data-testid={`package-card-${s.slug}`}
      className={`relative flex flex-col rounded-2xl p-6 transition-all hover:-translate-y-1 hover:shadow-xl ${
        highlight
          ? "bg-white border-2 border-[#F7941D] shadow-2xl ring-4 ring-[#F7941D]/10"
          : "bg-white border border-[#1a3c5e]/15 shadow-sm"
      }`}
    >
      {showBadge && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-white text-[10px] font-extrabold uppercase tracking-wider flex items-center gap-1 shadow-lg whitespace-nowrap"
          style={{ backgroundColor: badgeColor }}
        >
          ◆ {badgeLabel}
        </div>
      )}

      <div className="flex items-center flex-wrap gap-2 mb-1">
        <span className="font-display font-extrabold text-[#1a3c5e] text-2xl tracking-tight uppercase">{s.slug}</span>
        {discount > 0 && (
          <span className="px-2 py-0.5 rounded-full bg-[#D32F2F] text-white text-[10px] font-extrabold">{discount}% OFF</span>
        )}
        <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-[#1a3c5e]/20 text-[#1a3c5e]/80 flex items-center gap-1">
          <span className="text-[#F7941D]">◉</span> {s.slug === "luxury" ? "G+3" : "G+2"}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-5 min-h-[2.5rem]">{s.tagline}</p>

      <div className="rounded-xl bg-[#FEF3E2]/60 border border-[#F7941D]/20 p-4 mb-5">
        <div className="flex items-center gap-2 text-[#1a3c5e]">
          <span className="text-[#F7941D]">✦</span>
          <span className="font-display font-bold">{s.delivery_text}</span>
        </div>
        <div className="text-xs text-[#1a3c5e]/70 mt-1">= {s.revisions} Revisions Included</div>
        <div className="text-xs text-[#1a3c5e]/70">≈ {s.includes_list.length} deliverables included</div>
        <div className="mt-3 rounded-lg bg-white border border-[#1a3c5e]/10 px-3 py-2 text-xs text-[#1a3c5e]/80 flex items-center gap-1.5">
          <Check className="w-3 h-3 text-emerald-600" /> One-time payment · No subscription
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          {s.original_price > s.price && (
            <span className="text-lg text-gray-400 line-through font-bold">{inr(s.original_price)}</span>
          )}
          <span className={`font-display text-4xl font-extrabold ${theme.accent}`}>{inr(s.price)}</span>
        </div>
        <div className="text-xs text-gray-500 mt-0.5">one-time, fully online</div>
      </div>

      <button className={`w-full py-3 rounded-lg font-bold text-sm transition shadow-md ${theme.btn}`}>
        Get Plan
      </button>
      <div className="text-xs text-center mt-2 text-gray-500">
        {highlight ? `Save ${inr(s.original_price - s.price)} vs market rate` : `From ${inr(s.emi_per_month)}/mo via Snapmint`}
      </div>

      <div className="mt-5 pt-5 border-t border-[#1a3c5e]/10 space-y-2.5">
        {s.includes_list.slice(0, 8).map((item, i) => (
          <div key={i} className="flex gap-2 text-sm text-gray-700">
            <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>{item}</span>
          </div>
        ))}
        {s.includes_list.length > 8 && (
          <div className="text-xs text-[#F7941D] font-semibold pt-1">+ {s.includes_list.length - 8} more features</div>
        )}
      </div>
    </Link>
  );
}

function CustomPlanCard() {
  return (
    <a
      href="https://wa.me/919050785528"
      target="_blank"
      rel="noopener noreferrer"
      data-testid="package-card-custom"
      className="relative flex flex-col rounded-2xl p-6 bg-white border border-[#1a3c5e]/15 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="flex items-center flex-wrap gap-2 mb-1">
        <span className="font-display font-extrabold text-[#1a3c5e] text-2xl tracking-tight uppercase">For Builders</span>
        <span className="px-2 py-0.5 rounded-full bg-[#1a3c5e] text-white text-[10px] font-extrabold">CUSTOM</span>
      </div>
      <p className="text-sm text-gray-600 mb-5 min-h-[2.5rem]">For agencies, builders & multi-floor commercial projects</p>

      <div className="rounded-xl bg-[#FEF3E2]/60 border border-[#F7941D]/20 p-4 mb-5">
        <div className="flex items-center gap-2 text-[#1a3c5e]">
          <span className="text-[#F7941D]">✦</span>
          <span className="font-display font-bold">Tailored Timeline</span>
        </div>
        <div className="text-xs text-[#1a3c5e]/70 mt-1">= Unlimited revisions</div>
        <div className="text-xs text-[#1a3c5e]/70">= Volume discounts available</div>
        <div className="mt-3 rounded-lg bg-white border border-[#1a3c5e]/10 px-3 py-2 text-xs text-[#1a3c5e]/80 flex items-center gap-1.5">
          <Check className="w-3 h-3 text-emerald-600" /> Dedicated architect + WhatsApp group
        </div>
      </div>

      <div className="mb-4">
        <div className="font-display text-4xl font-extrabold text-[#1a3c5e]">Talk to us</div>
        <div className="text-xs text-gray-500 mt-0.5">Custom quote within 1 hour</div>
      </div>

      <button className="w-full py-3 rounded-lg font-bold text-sm bg-[#1a3c5e] hover:bg-[#122a42] text-white transition shadow-md">
        Contact Sales
      </button>
      <div className="text-xs text-center mt-2 text-gray-500">Reply within 15 min on WhatsApp</div>

      <div className="mt-5 pt-5 border-t border-[#1a3c5e]/10 space-y-2.5">
        {[
          "Multi-floor & commercial buildings",
          "G+4 and above structures",
          "Bulk plot subdivision layouts",
          "Township master planning",
          "Dedicated project manager",
          "Priority delivery (rush option)",
          "On-site supervision available",
          "Custom MOUs / NDAs supported",
        ].map((x, i) => (
          <div key={i} className="flex gap-2 text-sm text-gray-700">
            <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>{x}</span>
          </div>
        ))}
      </div>
    </a>
  );
}

// Feature comparison matrix — what each package includes
const COMPARE_FEATURES = [
  { group: "Drawings", items: [
    { label: "2D Floor Plan", basic: true, premium: true, luxury: true },
    { label: "Structural & Foundation Details", basic: true, premium: true, luxury: true },
    { label: "Electrical Layout", basic: true, premium: true, luxury: true },
    { label: "Plumbing & Sanitary Layout", basic: true, premium: true, luxury: true },
    { label: "Door & Window Details", basic: true, premium: true, luxury: true },
    { label: "Staircase & Section Details", basic: false, premium: true, luxury: true },
    { label: "Custom Joinery & Glazing Details", basic: false, premium: false, luxury: true },
    { label: "Automation-ready Electrical", basic: false, premium: false, luxury: true },
  ]},
  { group: "3D Renders", items: [
    { label: "3D Elevation (Front View)", basic: false, premium: true, luxury: true },
    { label: "3D All Views (Sides, Back, Aerial)", basic: false, premium: false, luxury: true },
    { label: "Interior 3D Views", basic: false, premium: false, luxury: true },
    { label: "Premium Photorealistic Render", basic: false, premium: false, luxury: true },
  ]},
  { group: "Support & Service", items: [
    { label: "WhatsApp Support", basic: true, premium: true, luxury: true },
    { label: "Vastu Compliance", basic: true, premium: true, luxury: true },
    { label: "Online Site Review Call", basic: false, premium: true, luxury: true },
    { label: "Video Call Site Supervision", basic: false, premium: false, luxury: true },
    { label: "Floors Supported", basic: "G+2", premium: "G+2", luxury: "G+3" },
    { label: "Revisions Included", basic: "2", premium: "3", luxury: "Unlimited 2D + 2 (3D)" },
    { label: "Delivery Time", basic: "7-10 days", premium: "10-15 days", luxury: "15-25 days" },
  ]},
];

function CompareCell({ value, highlight = false }) {
  const cls = `px-3 py-3 text-center ${highlight ? "bg-[#F7941D]/10 border-x-2 border-[#F7941D]/30" : ""}`;
  if (value === true) {
    return <td className={cls}><Check className="w-5 h-5 text-emerald-600 mx-auto" /></td>;
  }
  if (value === false) {
    return <td className={cls}><X className="w-5 h-5 text-gray-300 mx-auto" /></td>;
  }
  return <td className={cls}><span className="text-xs font-semibold text-[#1a3c5e]">{value}</span></td>;
}

function WhatsAppReview({ name, city, text }) {
  return (
    <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 shadow-sm overflow-hidden hover-lift" data-testid={`review-card-${name.split(' ')[0].toLowerCase()}`}>
      <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-semibold text-sm">
          {name[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{name}</div>
          <div className="text-[11px] text-white/80">online</div>
        </div>
        <Phone className="w-4 h-4 opacity-70" />
      </div>
      <div className="bg-[#ECE5DD] px-4 py-5 min-h-[180px]" style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22100%22 height=%22100%22 viewBox=%220 0 100 100%22><circle cx=%2220%22 cy=%2220%22 r=%221%22 fill=%22%23ddd%22/><circle cx=%2270%22 cy=%2250%22 r=%221%22 fill=%22%23ddd%22/></svg>')" }}>
        <div className="ml-3 wa-bubble max-w-[95%]">
          <p className="text-sm leading-relaxed">{text}</p>
          <div className="text-[10px] text-gray-500 mt-1.5 text-right">
            10:24 AM <span className="text-blue-500">✓✓</span>
          </div>
        </div>
      </div>
      <div className="bg-white px-4 py-3 flex items-center justify-between">
        <div>
          <div className="font-semibold text-[#1a3c5e] text-sm">{name}</div>
          <div className="text-xs text-gray-500">{city}</div>
        </div>
        <div className="flex gap-0.5">
          {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-[#F7941D] text-[#F7941D]" />)}
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [filter, setFilter] = useState("all");
  const [reviews, setReviews] = useState([]);
  const navigate = useNavigate();
  const settings = useSettings();

  useEffect(() => {
    api.get("/services").then(r => setServices(r.data));
    api.get("/portfolio").then(r => setPortfolio(r.data));
    api.get("/reviews/all").then(r => setReviews(r.data.slice(0, 4)));
  }, []);

  const filteredPortfolio = filter === "all" ? portfolio : portfolio.filter(p => p.category === filter);

  return (
    <>
      {/* HERO */}
      <section className="relative bg-[#FEF3E2] overflow-hidden">
        <div className="absolute -top-32 -right-24 w-96 h-96 bg-[#F7941D]/30 blob rounded-full" />
        <div className="absolute -bottom-32 -left-24 w-96 h-96 bg-[#1a3c5e]/15 blob rounded-full" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white border border-[#F7941D]/30 rounded-full text-xs font-semibold text-[#1a3c5e] shadow-sm mb-6" data-testid="hero-badge">
              <Award className="w-3.5 h-3.5 text-[#F7941D]" />
              Trusted by 500+ Homeowners Across India
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-[#1a3c5e]">
              {settings.hero_headline}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-gray-700 leading-relaxed max-w-xl font-semibold">
              {settings.hero_subtext}
            </p>

            <div className="inline-flex items-center gap-3 mt-6 px-5 py-3 bg-white border-2 border-[#F7941D] rounded-2xl shadow-md" data-testid="hero-price-badge">
              <div className="font-display text-xl sm:text-2xl font-extrabold text-[#F7941D]">{settings.hero_price_badge}</div>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={() => navigate("/contact")} data-testid="hero-quote-btn" className="px-7 py-3.5 bg-[#F7941D] hover:bg-[#e0861a] text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2">
                Get Quote Now <ArrowRight className="w-4 h-4" />
              </button>
              <a href={`https://wa.me/${settings.whatsapp_number}`} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn" className="px-7 py-3.5 bg-[#25D366] hover:bg-[#128C7E] text-white font-semibold rounded-xl shadow-lg transition-all flex items-center gap-2">
                <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10 text-sm">
              {[
                { i: <Globe className="w-4 h-4" />, t: "100% Online" },
                { i: <ShieldCheck className="w-4 h-4" />, t: "Transparent Process" },
                { i: <MessageCircle className="w-4 h-4" />, t: "Pan India" },
                { i: <Users className="w-4 h-4" />, t: "500+ Clients" },
              ].map((b, idx) => (
                <div key={idx} className="bg-white rounded-xl px-3 py-3 border border-[#1a3c5e]/10 shadow-sm flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-[#FEF3E2] flex items-center justify-center text-[#F7941D]">{b.i}</div>
                  <div className="text-xs font-semibold text-[#1a3c5e]">{b.t}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-white">
              <img
                src={settings.hero_banner_image}
                alt="HouseDesignWala — India's Expert Construction Drawing Platform"
                className="w-full h-auto object-cover"
                data-testid="hero-banner-image"
              />
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* PACKAGES — light theme, Higgsfield-style cards + comparison table */}
      <section id="packages" className="relative py-20 lg:py-24 bg-white overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[500px] bg-[#F7941D]/10 blur-[120px] rounded-full pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Promo banner */}
          <div className="relative rounded-2xl overflow-hidden border border-[#F7941D]/30 bg-gradient-to-r from-[#FEF3E2] via-[#FEF3E2]/60 to-white p-6 sm:p-8 mb-14">
            <div className="absolute right-0 top-0 bottom-0 w-1/2 opacity-30 pointer-events-none">
              <div className="absolute top-1/2 right-10 -translate-y-1/2 w-32 h-32 rounded-full border-4 border-[#F7941D]/40" />
              <div className="absolute top-1/3 right-32 w-20 h-20 rounded-full border-2 border-[#D32F2F]/40" />
            </div>
            <div className="relative">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D32F2F] text-white text-[10px] font-extrabold uppercase tracking-wider">
                ◆ Special 10% OFF
              </span>
              <h3 className="mt-3 font-display text-2xl sm:text-4xl font-extrabold text-[#1a3c5e] leading-tight">
                FIRST ORDER 10% OFF on ALL PACKAGES. <br className="hidden sm:block" />
                <span className="text-[#F7941D]">Code: FIRST10 — valid this week only</span>
              </h3>
              <p className="mt-2 text-sm text-gray-600 max-w-2xl">Get any package (Basic, Premium, Luxury) with 10% off this week. Use code FIRST10 at checkout. Plus EMI starts ₹2,833/month via Snapmint.</p>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1a3c5e] tracking-tight">Pick your plan</h2>
            <p className="mt-3 text-gray-600 max-w-xl mx-auto">Scale your home design with higher detail, priority delivery, and dedicated 3D views.</p>

            <div className="inline-flex items-center gap-3 mt-8 px-2 py-2 rounded-full bg-[#FEF3E2]/60 border border-[#1a3c5e]/10">
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold text-white bg-[#1a3c5e]">One-time</span>
              <span className="px-4 py-1.5 rounded-full text-sm font-semibold text-[#1a3c5e]/60">Bundle of 2+ <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#D32F2F] text-white text-[10px] font-bold">15% OFF</span></span>
            </div>
          </div>

          {/* Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {services.map((s, i) => <PackageCard key={s.slug} s={s} idx={i} />)}
            <CustomPlanCard />
          </div>

          {/* Comparison Table */}
          <div className="mt-20">
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F7941D]">Compare</span>
              <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1a3c5e] mt-2">What&apos;s in every package</h3>
              <p className="mt-2 text-sm text-gray-600">Pick the right level of detail for your project.</p>
            </div>

            <div className="bg-white rounded-2xl border border-[#1a3c5e]/15 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[700px] text-sm">
                  {/* Header */}
                  <thead className="bg-[#FEF3E2]/40 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-5 py-4 font-display font-bold text-[#1a3c5e] w-2/5">Feature</th>
                      <th className="px-3 py-4 text-center">
                        <div className="font-display font-extrabold text-[#1a3c5e] uppercase text-base">Basic</div>
                        <div className="text-xs text-[#F7941D] font-bold">{inr(8500)}</div>
                      </th>
                      <th className="px-3 py-4 text-center bg-[#F7941D]/10 border-x-2 border-[#F7941D]/30 relative">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#F7941D] text-white text-[9px] font-extrabold uppercase tracking-wider">Popular</div>
                        <div className="font-display font-extrabold text-[#1a3c5e] uppercase text-base">Premium</div>
                        <div className="text-xs text-[#F7941D] font-bold">{inr(13500)}</div>
                      </th>
                      <th className="px-3 py-4 text-center">
                        <div className="font-display font-extrabold text-[#1a3c5e] uppercase text-base">Luxury</div>
                        <div className="text-xs text-[#D32F2F] font-bold">{inr(20500)}</div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_FEATURES.map((group, gi) => (
                      <React.Fragment key={gi}>
                        <tr className="bg-[#1a3c5e]/5">
                          <td colSpan={4} className="px-5 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1a3c5e]/70">
                            {group.group}
                          </td>
                        </tr>
                        {group.items.map((row, ri) => (
                          <tr key={ri} className="border-t border-[#1a3c5e]/10 hover:bg-[#FEF3E2]/30">
                            <td className="px-5 py-3 text-gray-700">{row.label}</td>
                            <CompareCell value={row.basic} />
                            <CompareCell value={row.premium} highlight />
                            <CompareCell value={row.luxury} />
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                    {/* CTA row */}
                    <tr className="border-t border-[#1a3c5e]/10 bg-[#FEF3E2]/30">
                      <td className="px-5 py-4 font-semibold text-[#1a3c5e]">Choose this plan</td>
                      <td className="px-3 py-4 text-center">
                        <Link to="/services/basic"><button data-testid="compare-cta-basic" className="px-3 py-1.5 bg-[#1a3c5e] hover:bg-[#122a42] text-white text-xs font-bold rounded-lg">Get Basic</button></Link>
                      </td>
                      <td className="px-3 py-4 text-center bg-[#F7941D]/10 border-x-2 border-[#F7941D]/30">
                        <Link to="/services/premium"><button data-testid="compare-cta-premium" className="px-3 py-1.5 bg-[#F7941D] hover:bg-[#e0861a] text-white text-xs font-bold rounded-lg">Get Premium</button></Link>
                      </td>
                      <td className="px-3 py-4 text-center">
                        <Link to="/services/luxury"><button data-testid="compare-cta-luxury" className="px-3 py-1.5 bg-[#D32F2F] hover:bg-[#b71c1c] text-white text-xs font-bold rounded-lg">Get Luxury</button></Link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">All packages include free WhatsApp support, secure Razorpay payment, and 100% online service across India.</p>
          </div>

          <div className="text-center mt-10 space-y-1.5">
            <p className="text-sm text-[#1a3c5e] font-semibold">🎉 First Order? Use code <span className="bg-[#FEF3E2] text-[#F7941D] px-2 py-0.5 rounded font-bold">FIRST10</span> for 10% OFF · Secure Razorpay payment</p>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="py-20 lg:py-24 bg-[#FEF3E2]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F7941D]">Portfolio</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a3c5e] mt-2">Our Work</h2>
            <p className="mt-3 text-gray-600">Real projects for real homeowners across India</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {[
              { id: "all", label: "All" },
              { id: "2d", label: "2D Floor Plans" },
              { id: "3d", label: "3D Elevation" },
              { id: "construction", label: "Construction Drawings" },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                data-testid={`portfolio-filter-${t.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === t.id
                    ? "bg-[#1a3c5e] text-white shadow-md"
                    : "bg-white border border-[#1a3c5e]/15 text-[#1a3c5e] hover:bg-[#FEF3E2]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filteredPortfolio.map((p) => (
              <Link key={p.id} to={`/portfolio/${p.id}`} className="group relative aspect-[4/5] rounded-2xl overflow-hidden cursor-pointer shadow-md hover-lift bg-white block" data-testid={`portfolio-card-${p.id}`}>
                <img src={p.image} alt={p.client} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a3c5e]/95 via-[#1a3c5e]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-4 text-white">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#F7941D]">{p.service}</span>
                  <div className="font-display font-bold text-lg leading-tight mt-1">{p.client}</div>
                  <div className="text-xs text-white/80">{p.city}, {p.state}</div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/portfolio">
              <button className="px-6 py-3 rounded-xl font-semibold border-2 border-[#1a3c5e] text-[#1a3c5e] hover:bg-[#1a3c5e] hover:text-white transition" data-testid="view-all-portfolio">
                View Full Portfolio →
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHATSAPP REVIEWS */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F7941D]">Testimonials</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a3c5e] mt-2">What Our Clients Say</h2>
            <p className="mt-3 text-gray-600">Real conversations, real results from happy homeowners.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.map((r, i) => (
              <WhatsAppReview key={i} name={r.reviewer_name} city={r.city} text={r.review_text} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#F7941D] to-[#e87502] text-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <Quote className="w-10 h-10 mx-auto mb-4 opacity-50" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Ready to Build Your Dream Home?</h2>
          <p className="mt-3 text-base sm:text-lg text-white/90">Join 500+ happy homeowners across India.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <Link to="/services">
              <button className="px-7 py-3.5 bg-white text-[#F7941D] font-bold rounded-xl shadow-lg hover:shadow-xl transition-all" data-testid="cta-order">Order Now →</button>
            </Link>
            <a href="https://wa.me/919050785528" target="_blank" rel="noopener noreferrer">
              <button className="px-7 py-3.5 bg-[#1a3c5e] hover:bg-[#122a42] text-white font-bold rounded-xl shadow-lg transition-all flex items-center gap-2" data-testid="cta-whatsapp">
                <MessageCircle className="w-5 h-5" /> Chat on WhatsApp
              </button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
